import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import Header from "./Header";
import Footer from "./Footer";
import FaqSection from "components/PublicPagesReusable/FaqSection";
import "assets/css/PteScoreCore.css";

/* Official Pearson PTE Core -> CLB. rows: [clb, min, max] high->low */
const CORE = {
  Listening: [[10, 89, 90], [9, 82, 88], [8, 71, 81], [7, 60, 70], [6, 50, 59], [5, 39, 49], [4, 28, 38], [3, 18, 27]],
  Reading: [[10, 88, 90], [9, 78, 87], [8, 69, 77], [7, 60, 68], [6, 51, 59], [5, 42, 50], [4, 33, 41], [3, 24, 32]],
  Speaking: [[10, 89, 90], [9, 84, 88], [8, 76, 83], [7, 68, 75], [6, 59, 67], [5, 51, 58], [4, 42, 50], [3, 34, 41]],
  Writing: [[10, 90, 90], [9, 88, 89], [8, 79, 87], [7, 69, 78], [6, 60, 68], [5, 51, 59], [4, 41, 50], [3, 32, 40]],
};

const SK = ["Listening", "Reading", "Speaking", "Writing"];
const AB = { Listening: "L", Reading: "R", Speaking: "S", Writing: "W" };

const clbOf = (skill, score) => {
  const r = CORE[skill];
  const mx = r[0][2];
  for (let i = 0; i < r.length; i++) {
    if (score >= r[i][1] && score <= r[i][2]) return r[i][0];
  }
  return score > mx ? 10 : 0;
};

const clbMin = (skill, clb) => {
  const r = CORE[skill];
  for (let i = 0; i < r.length; i++) {
    if (r[i][0] === clb) return r[i][1];
  }
  return null;
};

const pct = (clb) => Math.max(0, Math.min(1, (clb - 3) / 7));
const trackLeft = (p) => `calc(12px + (100% - 24px) * ${p})`;

const rowDisplay = (skill, raw) => {
  const value = (raw || "").trim();
  if (value === "") return { bad: false, text: "—", vClass: "v dim" };
  const n = parseFloat(value);
  if (Number.isNaN(n) || n < 10 || n > 90) {
    return { bad: true, text: "—", vClass: "v dim" };
  }
  const c = clbOf(skill, n);
  return {
    bad: false,
    text: c === 0 ? "< 3" : String(c),
    vClass: "v" + (c >= 9 ? " good" : c < 7 ? " low" : ""),
  };
};

const computeMarkers = (clbs) => {
  const groups = {};
  SK.forEach((s) => {
    const c = clbs[s];
    if (c === undefined) return;
    (groups[String(c)] = groups[String(c)] || []).push(s);
  });
  const info = {};
  Object.keys(groups).forEach((key) => {
    const list = groups[key];
    const n = list.length;
    list.forEach((s, i) => {
      const c = clbs[s];
      const p = c === 0 ? 0 : pct(c);
      info[s] = {
        show: true,
        left: p,
        ox: (i - (n - 1) / 2) * 11,
        good: c >= 9,
        low: c < 7,
      };
    });
  });
  return info;
};

const SCALE_TICKS = (() => {
  const ticks = [];
  for (let c = 3; c <= 10; c++) {
    const em = c === 7 || c === 9;
    ticks.push({
      c,
      em,
      left: trackLeft(pct(c)),
      cap: c === 7 ? "min" : c === 9 ? "max pts" : null,
    });
  }
  return ticks;
})();

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "EducationalOrganization", "@id": "https://www.languageacademy.com.au/#organization", name: "Language Academy", url: "https://www.languageacademy.com.au/", logo: { "@type": "ImageObject", url: "https://www.languageacademy.com.au/assets/la-logo.png" }, telephone: "+61426230061", email: "info@languageacademy.com.au" },
    { "@type": "WebApplication", "@id": "https://www.languageacademy.com.au/pte-core-score-calculator/#app", name: "PTE Core Score Calculator", url: "https://www.languageacademy.com.au/pte-core-score-calculator/", applicationCategory: "EducationApplication", operatingSystem: "All (web browser)", description: "Free PTE Core score calculator. Type your module scores to see each skill's CLB level, your governing CLB for Express Entry, and what you need for CLB 7 and CLB 9.", inLanguage: "en-AU", isAccessibleForFree: true, offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" }, creator: { "@id": "https://www.languageacademy.com.au/#organization" } },
    { "@type": "FAQPage", "@id": "https://www.languageacademy.com.au/pte-core-score-calculator/#faq", mainEntity: [
      { "@type": "Question", name: "How do I convert my PTE Core score to CLB?", acceptedAnswer: { "@type": "Answer", text: "Type your four module scores from your PTE Core score report into the calculator - each one instantly shows its CLB level, and your governing CLB for Express Entry appears below." } },
      { "@type": "Question", name: "What PTE Core score do I need for CLB 7?", acceptedAnswer: { "@type": "Answer", text: "CLB 7 requires Listening 60, Reading 60, Speaking 68 and Writing 69 in PTE Core." } },
      { "@type": "Question", name: "What PTE Core score do I need for CLB 9?", acceptedAnswer: { "@type": "Answer", text: "CLB 9 requires Listening 82, Reading 78, Speaking 84 and Writing 88 in PTE Core." } },
      { "@type": "Question", name: "How is my overall CLB decided?", acceptedAnswer: { "@type": "Answer", text: "Your usable CLB for Express Entry is your lowest of the four skills, not an average." } },
      { "@type": "Question", name: "Is PTE Core the same as PTE Academic?", acceptedAnswer: { "@type": "Answer", text: "No. PTE Core is for Canadian immigration and maps to CLB; PTE Academic is for study and Australian migration." } },
      { "@type": "Question", name: "Can I use PTE Core for Canadian PR / Express Entry?", acceptedAnswer: { "@type": "Answer", text: "Yes. PTE Core is approved by IRCC for Canadian economic-class immigration." } },
      { "@type": "Question", name: "Can I use PTE Core to study in Canada?", acceptedAnswer: { "@type": "Answer", text: "PTE Core is designed for immigration, not study permits. Institutions generally require PTE Academic." } },
      { "@type": "Question", name: "How long is a PTE Core score valid?", acceptedAnswer: { "@type": "Answer", text: "PTE Core scores are valid for two years from the test date." } },
      { "@type": "Question", name: "How do I check my real level before the exam?", acceptedAnswer: { "@type": "Answer", text: "Sit a free scored mock test on the Language Academy AI portal for instant AI scoring and module-by-module feedback." } },
    ] },
  ],
};

const PteScoreCore = () => {
  const [scores, setScores] = useState({
    Listening: "",
    Reading: "",
    Speaking: "",
    Writing: "",
  });
  const [coachMounted, setCoachMounted] = useState(false);
  const [coachShown, setCoachShown] = useState(false);

  const listeningRef = useRef(null);
  const coachDoneRef = useRef(false);

  const { clbs, scoreNums, count, moduleRows, lowest, capSkill } = useMemo(() => {
    const _clbs = {};
    const _scoreNums = {};
    const _moduleRows = {};
    SK.forEach((s) => {
      _moduleRows[s] = rowDisplay(s, scores[s]);
      const value = (scores[s] || "").trim();
      if (value === "") return;
      const n = parseFloat(value);
      if (Number.isNaN(n) || n < 10 || n > 90) return;
      _scoreNums[s] = n;
      _clbs[s] = clbOf(s, n);
    });

    const c = Object.keys(_scoreNums).length;
    let _lowest = 99;
    let _capSkill = "";
    if (c === 4) {
      SK.forEach((s) => {
        if (_clbs[s] < _lowest) {
          _lowest = _clbs[s];
          _capSkill = s;
        }
      });
    }

    return {
      clbs: _clbs,
      scoreNums: _scoreNums,
      count: c,
      moduleRows: _moduleRows,
      lowest: c === 4 ? _lowest : null,
      capSkill: c === 4 ? _capSkill : "",
    };
  }, [scores]);

  const markers = useMemo(() => computeMarkers(clbs), [clbs]);

  const governing = useMemo(() => {
    if (count < 4) return { ready: false };
    const txt = lowest === 0 ? "< 3" : String(lowest);
    let chipClass = "chip amber";
    let chipLabel = "Below CLB 5";
    if (lowest >= 9) {
      chipClass = "chip green";
      chipLabel = "Maximum CRS points";
    } else if (lowest >= 7) {
      chipClass = "chip green";
      chipLabel = "Meets FSW minimum";
    } else if (lowest >= 5) {
      chipClass = "chip amber";
      chipLabel = "Trades / CEC range";
    }
    return { ready: true, txt, chipClass, chipLabel, capSkill };
  }, [count, lowest, capSkill]);

  const ee = useMemo(() => {
    if (count < 4 || lowest === null) return { ready: false };
    const onLevel = lowest >= 9 ? "9" : lowest >= 7 ? "7" : lowest >= 5 ? "5" : null;
    if (lowest >= 9) {
      return { ready: true, segOn: onLevel, showWin: true };
    }
    const target = lowest >= 7 ? 9 : 7;
    const now = lowest >= 7
      ? "You meet <strong>CLB 7</strong> — the FSW minimum"
      : lowest >= 5
        ? `You're at <strong>CLB ${lowest}</strong> — enough for some trades/CEC streams`
        : lowest === 0
          ? "You're <strong>below CLB 3</strong>"
          : `You're at <strong>CLB ${lowest}</strong>`;
    const tgt = target === 9 ? "CLB 9 (maximum points)" : "CLB 7 (Express Entry minimum)";
    const msgHtml = `${now}. For <strong>${tgt}</strong> you need:`;
    const needs = SK.map((s) => {
      const have = scoreNums[s];
      const req = clbMin(s, target);
      return have >= req
        ? { s, ok: true, have }
        : { s, ok: false, have, req, gap: req - have };
    });
    return { ready: true, segOn: onLevel, showWin: false, msgHtml, needs };
  }, [count, lowest, scoreNums]);

  const handleChange = useCallback((skill, value) => {
    setScores((prev) => ({ ...prev, [skill]: value }));
  }, []);

  const dismissCoach = useCallback((mark) => {
    if (coachDoneRef.current) return;
    coachDoneRef.current = true;
    setCoachShown(false);
    if (mark) {
      try {
        localStorage.setItem("laCoachSeen", "1");
      } catch (e) {
        /* ignore */
      }
    }
    setTimeout(() => setCoachMounted(false), 350);
  }, []);

  const handleReset = useCallback(() => {
    setScores({ Listening: "", Reading: "", Speaking: "", Writing: "" });
    if (listeningRef.current) listeningRef.current.focus();
  }, []);

  const handleSample = useCallback(() => {
    setScores({ Listening: "82", Reading: "70", Speaking: "68", Writing: "69" });
    dismissCoach(true);
  }, [dismissCoach]);

  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem("laCoachSeen") === "1";
    } catch (e) {
      /* ignore */
    }
    if (seen) return undefined;
    setCoachMounted(true);
    const t1 = setTimeout(() => {
      if (!coachDoneRef.current) setCoachShown(true);
    }, 700);
    const t2 = setTimeout(() => dismissCoach(false), 11000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [dismissCoach]);

  useEffect(() => {
    const els = document.querySelectorAll(".pte-score-core-page .reveal");
    if (!els.length) return undefined;
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("in");
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
      );
      els.forEach((el) => io.observe(el));
      return () => io.disconnect();
    }
    els.forEach((el) => el.classList.add("in"));
    return undefined;
  }, []);

  const renderMarker = (s) => {
    const m = markers[s];
    const cls = ["mk", m && m.show ? "show" : "", m && m.good ? "good" : "", m && m.low ? "low" : ""]
      .filter(Boolean)
      .join(" ");
    const style = m ? { left: trackLeft(m.left), "--ox": `${m.ox}px` } : undefined;
    return (
      <div key={s} className={cls} style={style} aria-hidden="true">
        {AB[s]}
      </div>
    );
  };

  const faqQuestions = [
    {
      question: "How do I convert my PTE Core score to CLB?",
      answer:
        "Type your four module scores (Listening, Reading, Speaking, Writing) from your PTE Core score report into the calculator above — each one instantly shows its CLB level, and your governing CLB for Express Entry appears below.",
    },
    {
      question: "What PTE Core score do I need for CLB 7?",
      answer: (
        <>
          CLB 7 requires <strong>Listening 60, Reading 60, Speaking 68 and Writing 69</strong> in PTE Core. CLB 7 is the common minimum for the Federal Skilled Worker stream and most Express Entry programs.
        </>
      ),
    },
    {
      question: "What PTE Core score do I need for CLB 9?",
      answer: (
        <>
          CLB 9 requires <strong>Listening 82, Reading 78, Speaking 84 and Writing 88</strong> in PTE Core. CLB 9 in all abilities earns the maximum CRS points for language — the target for a competitive profile.
        </>
      ),
    },
    {
      question: "How is my overall CLB decided?",
      answer: (
        <>
          Your usable CLB for Express Entry is your <strong>lowest</strong> of the four skills, not an average. If you score CLB 9 in three skills but CLB 7 in one, your governing CLB is 7 — so balancing all four abilities matters.
        </>
      ),
    },
    {
      question: "Is PTE Core the same as PTE Academic?",
      answer: (
        <>
          No. PTE Core is for Canadian immigration and maps to CLB; PTE Academic is for study and Australian migration and maps to IELTS. They share the 10–90 scale, but Core replaces the academic essay with a Write Email task. For Australia, use our <a href="/pte-score-calculator/academic">PTE Academic calculator</a>.
        </>
      ),
    },
    {
      question: "Can I use PTE Core for Canadian PR / Express Entry?",
      answer:
        "Yes. PTE Core is approved by IRCC for Canadian economic-class immigration, including the Federal Skilled Worker, Federal Skilled Trades and Canadian Experience Class programs.",
    },
    {
      question: "Can I use PTE Core to study in Canada?",
      answer:
        "PTE Core is designed for immigration, not study permits. For Canadian university admission and study permits, institutions generally require PTE Academic. Check your institution's current requirements.",
    },
    {
      question: "How long is a PTE Core score valid?",
      answer:
        "PTE Core scores are valid for two years from the test date. For Express Entry, your language results must be less than two years old when you submit your profile and when you're invited to apply.",
    },
    {
      question: "How do I check my real level before the exam?",
      answer: (
        <>
          Sit a free scored mock test on the Language Academy AI portal. You'll get instant AI scoring on real exam-style questions, module-by-module feedback, and a clear picture of which skill is capping your CLB — <a href="https://www.languageacademy.com.au/mock-test">start free here</a>.
        </>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <html lang="en-AU" />
        <title>PTE Core Score Calculator | PTE Core to CLB — Language Academy</title>
        <meta
          name="description"
          content="Free PTE Core score calculator — see the CLB level for every module, your governing CLB for Express Entry, and exactly what you need for CLB 7 and CLB 9."
        />
        <link rel="canonical" href="https://www.languageacademy.com.au/pte-core-score-calculator/" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Language Academy" />
        <meta property="og:title" content="PTE Core Score Calculator — PTE Core to CLB" />
        <meta
          property="og:description"
          content="Type your PTE Core scores to see each module's CLB level, your governing CLB for Canadian Express Entry, and what you need for CLB 7 and CLB 9."
        />
        <meta property="og:url" content="https://www.languageacademy.com.au/pte-core-score-calculator/" />
        <meta property="og:image" content="https://www.languageacademy.com.au/assets/pte-core-score-calculator-og.png" />
        <meta property="og:locale" content="en_AU" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PTE Core Score Calculator — PTE Core to CLB" />
        <meta
          name="twitter:description"
          content="See each PTE Core module's CLB, your governing CLB for Express Entry, and what you need for CLB 7 and 9 — instantly."
        />
        <meta name="twitter:image" content="https://www.languageacademy.com.au/assets/pte-core-score-calculator-og.png" />
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>

      <Header />
      <div className="new-design">
      <div className="pte-score-core-page js">
        {/* ================= HERO ================= */}
        <section className="hero">
          <svg className="leaf l1" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2l1.8 3.6 3-1.2-.9 3.3 3.6.6-2.4 2.7 3 1.8-3.3 1.2.6 3.6-3.3-.9L12 22l-2.1-5.3-3.3.9.6-3.6L3.9 12.8l3-1.8-2.4-2.7 3.6-.6-.9-3.3 3 1.2L12 2z" />
          </svg>
          <svg className="leaf l2" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2l1.8 3.6 3-1.2-.9 3.3 3.6.6-2.4 2.7 3 1.8-3.3 1.2.6 3.6-3.3-.9L12 22l-2.1-5.3-3.3.9.6-3.6L3.9 12.8l3-1.8-2.4-2.7 3.6-.6-.9-3.3 3 1.2L12 2z" />
          </svg>
          <div className="wrap">
            <span className="hero-badge">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="#F0654F" aria-hidden="true">
                <path d="M12 2l1.8 3.6 3-1.2-.9 3.3 3.6.6-2.4 2.7 3 1.8-3.3 1.2.6 3.6-3.3-.9L12 22l-2.1-5.3-3.3.9.6-3.6L3.9 12.8l3-1.8-2.4-2.7 3.6-.6-.9-3.3 3 1.2L12 2z" />
              </svg>
              PTE Core · Canada Immigration (IRCC)
            </span>
            <h1>PTE Core <span className="accent">Score Calculator</span></h1>
            <p className="lede">
              <strong>Type the scores from your PTE Core score report</strong> — see the CLB level for every module, your governing CLB for Express Entry, and exactly what you need for CLB 7 and CLB 9.
            </p>
            <div className="hero-trust">
              <span className="stars" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#A9D54B">
                    <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9z" />
                  </svg>
                ))}
              </span>
              <span>Trusted by <b>50,000+ students</b></span>
            </div>
          </div>
        </section>

        {/* ================= CALCULATOR ================= */}
        <div className="wrap calc-zone" id="tool">
          <div className="report" aria-label="PTE Core score calculator">
            <div className="r-head">
              <div>
                <h2>Your scores, in CLB</h2>
                <div className="sub">Everything updates as you type — your governing CLB is set by your lowest module.</div>
              </div>
              <span className="r-badge">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 13l4 4L19 7" stroke="#557414" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Official Pearson PTE Core–CLB table · IRCC
              </span>
            </div>

            <div className="strip">
              <div className="strip-head">
                <span className="t">Your skills on the CLB scale</span>
                <span className="l">L R S W markers · CLB 3 – 10</span>
              </div>
              <div className="scale" id="scale" role="img" aria-label="Your PTE Core modules positioned on the CLB scale">
                <div className="s-track" />
                {SCALE_TICKS.map((t) => (
                  <React.Fragment key={t.c}>
                    <div className={`s-tick${t.em ? " em" : ""}`} style={{ left: t.left }} />
                    <div className={`s-lab${t.em ? " em" : ""}`} style={{ left: t.left }}>
                      {t.c}
                    </div>
                    {t.cap && (
                      <div className="s-cap" style={{ left: t.left }}>
                        {t.cap}
                      </div>
                    )}
                  </React.Fragment>
                ))}
                {SK.map(renderMarker)}
              </div>
            </div>

            <div className="calc-rows" id="rows">
              {SK.map((s) => {
                const row = moduleRows[s];
                return (
                  <div className={`calc-row${row.bad ? " bad" : ""}`} id={`row-${s}`} key={s}>
                    <div>
                      <div className="r-name">{s}</div>
                      <div className="r-hint">Enter 10–90</div>
                    </div>
                    <div className="r-in">
                      <input
                        type="number"
                        id={`in-${s}`}
                        min="10"
                        max="90"
                        step="1"
                        inputMode="numeric"
                        placeholder="–"
                        aria-label={`${s} PTE Core score`}
                        value={scores[s]}
                        ref={s === "Listening" ? listeningRef : undefined}
                        className={s === "Listening" && coachShown ? "pulse" : undefined}
                        onChange={(e) => handleChange(s, e.target.value)}
                        onFocus={() => dismissCoach(true)}
                      />
                      {s === "Listening" && coachMounted && (
                        <div className={`coach${coachShown ? " show" : ""}`} role="status">
                          Start here — type your scores straight from your <b>PTE Core score report</b>
                          <button type="button" aria-label="Dismiss tip" onClick={() => dismissCoach(true)}>
                            ×
                          </button>
                          <i className="c-arr" />
                        </div>
                      )}
                    </div>
                    <div className="r-out">
                      <span className="k">CLB</span>
                      <span className={row.vClass} id={`v-${s}`}>{row.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="govrow" aria-live="polite">
              <div className="gl">
                <div className="gk">Your governing CLB</div>
                <div className="gn">Set by your lowest skill — not an average</div>
              </div>
              <div className="gr" id="gov-r">
                {governing.ready ? (
                  <>
                    <span className="gov-big tnum">CLB {governing.txt}</span>
                    <span className={governing.chipClass}>{governing.chipLabel}</span>
                    <span className="chip">Capped by {governing.capSkill}</span>
                  </>
                ) : (
                  <span className="gov-big dim">— —</span>
                )}
              </div>
            </div>

            <div className="prb" aria-live="polite">
              <div className="prb-top">
                <span className="t">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#E0563F" aria-hidden="true">
                    <path d="M12 2l1.8 3.6 3-1.2-.9 3.3 3.6.6-2.4 2.7 3 1.8-3.3 1.2.6 3.6-3.3-.9L12 22l-2.1-5.3-3.3.9.6-3.6L3.9 12.8l3-1.8-2.4-2.7 3.6-.6-.9-3.3 3 1.2L12 2z" />
                  </svg>
                  Canada PR — Express Entry
                </span>
                <span className="s">
                  You must meet the CLB in <strong>every</strong> skill
                </span>
              </div>
              <div className="meter" id="meter">
                {[
                  { key: "5", p: "CLB 5", n: "Trades / CEC" },
                  { key: "7", p: "CLB 7", n: "FSW Minimum" },
                  { key: "9", p: "CLB 9", n: "Max CRS Points" },
                ].map((seg) => (
                  <div className={`seg${ee.ready && ee.segOn === seg.key ? " on" : ""}`} data-l={seg.key} key={seg.key}>
                    <div className="p">{seg.p}</div>
                    <div className="n">{seg.n}</div>
                  </div>
                ))}
              </div>

              {!ee.ready && (
                <div className="pr-empty" id="pr-empty">
                  Enter your four module scores to see your level, whether you meet CLB 7, and exactly what you still need.
                </div>
              )}

              {ee.ready && (
                <div id="pr-out">
                  {!ee.showWin && (
                    <p className="pr-msg" id="pr-msg" dangerouslySetInnerHTML={{ __html: ee.msgHtml }} />
                  )}
                  {!ee.showWin && (
                    <div className="needs" id="needs">
                      {ee.needs.map((nr) => (
                        <div className="nrow" key={nr.s}>
                          <span className="nm">{nr.s}</span>
                          {nr.ok ? (
                            <span className="vl ok tnum">{nr.have} — already there ✓</span>
                          ) : (
                            <span className="vl gap tnum">
                              {nr.have} → {nr.req}{"\u00A0\u00A0"}(+{nr.gap})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {ee.showWin && (
                    <div className="pr-win" id="pr-win">
                      CLB 9 in every skill — the maximum CRS language tier. Outstanding. ✓
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="cfoot">
              <span className="acts">
                <button className="linkbtn" id="sample" type="button" onClick={handleSample}>
                  Try sample scores
                </button>
                <button className="linkbtn" id="reset" type="button" onClick={handleReset}>
                  Clear
                </button>
              </span>
              <span className="src">Data: official Pearson PTE Core–CLB table · IRCC</span>
            </div>
          </div>

          <div className="goodtoknow">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="#557414" strokeWidth="1.8" />
              <path d="M12 8v5M12 16h.01" stroke="#557414" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span>
              <b>Good to know:</b> requirements and immigration policies change from time to time. For your specific pathway, always check the latest requirements with the official authority — IRCC (canada.ca) or a registered immigration consultant.
            </span>
          </div>
        </div>

        {/* ================= CHART ================= */}
        <section className="sec" id="chart">
          <div className="wrap narrow">
            <div className="sec-h reveal">
              <h2>PTE Core to CLB <span className="accent on-light">Conversion Chart</span></h2>
              <p>The official alignment between PTE Core and the Canadian Language Benchmarks, used by IRCC for Express Entry. Each skill converts on its own scale — that's why the calculator reads your modules separately.</p>
            </div>
            <div className="twrap reveal">
              <div className="tscroll">
                <table>
                  <caption className="sr-only">PTE Core to CLB conversion chart, per skill — official Pearson table used by IRCC</caption>
                  <thead>
                    <tr>
                      <th scope="col">CLB Level</th>
                      <th scope="col">Listening</th>
                      <th scope="col">Reading</th>
                      <th scope="col">Speaking</th>
                      <th scope="col">Writing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["CLB 10", "89–90", "88–90", "89–90", "90"],
                      ["CLB 9", "82–88", "78–87", "84–88", "88–89"],
                      ["CLB 8", "71–81", "69–77", "76–83", "79–87"],
                      ["CLB 7", "60–70", "60–68", "68–75", "69–78"],
                      ["CLB 6", "50–59", "51–59", "59–67", "60–68"],
                      ["CLB 5", "39–49", "42–50", "51–58", "51–59"],
                      ["CLB 4", "28–38", "33–41", "42–50", "41–50"],
                      ["CLB 3", "18–27", "24–32", "34–41", "32–40"],
                    ].map((r) => (
                      <tr key={r[0]}>
                        <td>{r[0]}</td>
                        <td className="key">{r[1]}</td>
                        <td>{r[2]}</td>
                        <td>{r[3]}</td>
                        <td>{r[4]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="tnote reveal">
              Studying or migrating to Australia instead? Use the <a href="/pte-score-calculator/">PTE Academic score calculator</a>.
            </p>
          </div>
        </section>

        {/* ================= TARGETS ================= */}
        <section className="sec" id="targets">
          <div className="wrap narrow">
            <div className="sec-h reveal">
              <h2>What CLB Do You Need <span className="accent on-light">For Canada?</span></h2>
              <p>Your CLB must be met in <strong>every</strong> ability — one weak skill caps your level. These are the PTE Core scores behind the three benchmarks that matter most.</p>
            </div>
            <div className="twrap reveal">
              <div className="tscroll">
                <table>
                  <caption className="sr-only">PTE Core scores needed per module for CLB 5, CLB 7 and CLB 9</caption>
                  <thead>
                    <tr>
                      <th scope="col">Target</th>
                      <th scope="col">Used For</th>
                      <th scope="col">Listening</th>
                      <th scope="col">Reading</th>
                      <th scope="col">Speaking</th>
                      <th scope="col">Writing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["CLB 9", "Maximum CRS language points", "82", "78", "84", "88"],
                      ["CLB 7", "Federal Skilled Worker minimum", "60", "60", "68", "69"],
                      ["CLB 5", "Trades / CEC (TEER 2–3)", "39", "42", "51", "51"],
                    ].map((r) => (
                      <tr key={r[0]}>
                        <td>{r[0]}</td>
                        <td>{r[1]}</td>
                        <td className={r[0] === "CLB 9" ? "key" : undefined}>{r[2]}</td>
                        <td className={r[0] === "CLB 9" ? "key" : undefined}>{r[3]}</td>
                        <td className={r[0] === "CLB 9" ? "key" : undefined}>{r[4]}</td>
                        <td className={r[0] === "CLB 9" ? "key" : undefined}>{r[5]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="tnote reveal">
              Canadian Experience Class: CLB 7 for TEER 0/1 jobs, CLB 5 for TEER 2/3. CLB 9+ also unlocks the highest skill-transferability points.
            </p>
          </div>
        </section>

        {/* ================= STATS ================= */}
        <section className="sec">
          <div className="wrap">
            <div className="stats reveal">
              <div className="stat">
                <div className="tile" style={{ background: "var(--tile-lav)" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3L2 8l10 5 10-5-10-5zM5 10.5V16c0 1.5 3 3.5 7 3.5s7-2 7-3.5v-5.5" stroke="#5B5BD6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="num tnum">50,000<span>+</span></div>
                <div className="lbl">Students' Enrolled</div>
              </div>
              <div className="stat">
                <div className="tile" style={{ background: "var(--tile-pink)" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M4 21V5a1 1 0 011-1h8a1 1 0 011 1v16M4 21h16M14 9h5a1 1 0 011 1v11M7.5 8h3M7.5 12h3M7.5 16h3" stroke="#D6566F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="num tnum">10<span>+</span></div>
                <div className="lbl">Learning Centres</div>
              </div>
              <div className="stat">
                <div className="tile" style={{ background: "var(--tile-mint)" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M3 17l5-5 4 4 8-8M15 8h5v5" stroke="#2F9B5C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="num tnum">95<span>%</span></div>
                <div className="lbl">Success Rate</div>
              </div>
              <div className="stat">
                <div className="tile" style={{ background: "var(--tile-peach)" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="9" r="5.5" stroke="#D98425" strokeWidth="1.8" />
                    <path d="M8.5 13.5L7 21l5-2.5L17 21l-1.5-7.5" stroke="#D98425" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="num tnum">8<span>+</span></div>
                <div className="lbl">Years Experience</div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= AI PORTAL ================= */}
        <section className="sec">
          <div className="wrap">
            <div className="ai reveal">
              <h2>Reach Your CLB Target with <span className="accent">AI Practice &amp; Guidance</span></h2>
              <p className="sub">A calculator shows where you stand — Australia's most accurate AI practice platform shows you how to get there. Real exam questions, instant AI scoring, and a study plan built around the module that's capping your CLB.</p>
              <div className="ai-feats">
                <div className="feat">
                  <div className="fi">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                      <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.8" />
                    </svg>
                  </div>
                  <h4>Accurate Scoring</h4>
                  <p>AI evaluation aligned with real exam standards — precise, unbiased results in minutes.</p>
                </div>
                <div className="feat">
                  <div className="fi">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M4 19V10M10 19V5M16 19v-7M21 19H3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h4>Detailed Performance Insights</h4>
                  <p>See strengths, weaknesses and exactly which question types are costing you marks.</p>
                </div>
                <div className="feat">
                  <div className="fi">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 3l2.2 4.8L19 9l-4.8 2.2L12 16l-2.2-4.8L5 9l4.8-1.2L12 3zM18 15l1 2.5L21.5 18l-2.5 1L18 21.5 17 19l-2.5-1 2.5-.5 1-2.5z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h4>Personalised Recommendations</h4>
                  <p>Targeted practice strategies that lift your lowest module — where your CLB is decided.</p>
                </div>
              </div>
              <div className="btns">
                <a className="pill pill-green pill-lg" href="https://www.languageacademy.com.au/auth/register">Start Free PTE Practice <span className="arr">↗</span></a>
                <a className="pill pill-out pill-lg" href="https://www.languageacademy.com.au/mock-test">Take a Free Mock Test <span className="arr">↗</span></a>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="py-5" id="faq">
        <FaqSection questions={faqQuestions} />
      </div>

      <div className="pte-score-core-page js">
        <section className="sec last">
          <div className="wrap narrow">
            <div className="sec-h reveal">
              <h2>Built on <span className="accent on-light">Official Data</span></h2>
            </div>
            <div className="srcs reveal">
              <a className="src" href="https://www.pearsonpte.com/pte-core" rel="noopener noreferrer" target="_blank">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M7 7h10v10M7 17L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Pearson — PTE Core to CLB table
              </a>
              <a className="src" href="https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry/documents/language-test.html" rel="noopener noreferrer" target="_blank">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M7 7h10v10M7 17L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                IRCC (canada.ca) — language tests &amp; CLB
              </a>
            </div>
          </div>
        </section>
      </div>
       </div>
      <Footer />
    </>
  );
};

export default PteScoreCore;
